import { useRouter } from "next/router";
import React, { useRef } from "react";
import styled from "styled-components";
import { useConfigContext } from "~/providers/ConfigContextProvider";
import { createUnsafeUUID } from "~/utils/createUnsafeUUID";
import { Heading } from "./Heading";

type TableHeader = {
  breakpointWidth?: Record<string, string>;
  textAlign?: string;
  label: string;
  visibleAt?:
    | "base"
    | "mobile"
    | "mobileLandscape"
    | "tablet"
    | "tabletLandscape"
    | "desktop"
    | "screen";
};

type TableCell = {
  visibleAt?: string;
  verticalAlign?: string;
  content: React.ReactNode | string | number;
};

type TableRow = {
  cells: TableCell[];
};

const StyledTableContainer = styled.div`
  width: 100%;
  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
      margin: 0 0 ${props.theme.spacePx(breakpoint, 5)} 0;          
    `;
    })}

  & img {
    display: block;
  }
  /* 
  @media (any-pointer: fine) {
    &:hover {
      & img {
        display: block;
      }
    }
  } */
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  overflow-wrap: break-word;
`;

const Tr = styled.tr<{ isClickable?: boolean; useImageHover?: boolean }>`
  background-color: var(--ikon-bg-color, #fff);

  cursor: ${({ isClickable }) => (isClickable ? "pointer" : "default")};

  ${({ useImageHover }) =>
    !useImageHover
      ? `
    @media (any-pointer: fine) {
      &:hover {
        & > td {
          background-color: var(--ikon-hl-color, #ff0);
        }
      }
    }
  `
      : ``}

  &.sticky {
    position: sticky;
    border-bottom: none;
    box-shadow: inset 0 -3px 0 black;
    vertical-align: bottom;
    z-index: 2;

    & th {
      box-shadow: inset 0 -3px 0 black;
    }
  }

  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
      &.sticky {
        top: ${props.theme.spacePx(breakpoint, 6)};
      }
    `;
    })}
`;

const Th = styled.th<{
  breakpointWidth?: Record<string, string>;
  textAlign?: string;
  visibleAt?: string;
  space?: number;
  sticky?: boolean;
}>`
  padding: 0;
  margin: 0;
  text-align: ${({ textAlign }) => textAlign ?? "left"};
  font-weight: bold;
  vertical-align: bottom;
  ${(props) =>
    props.theme.apply("all", (breakpoint: string) => {
      return `
        width: ${props?.breakpointWidth?.[breakpoint] ?? "auto"};
        padding-right: ${props.theme.spacePx(
          breakpoint,
          props.space ?? ["desktop", "screen"].includes(breakpoint)
            ? 7
            : !["base", "mobile"].includes(breakpoint)
            ? 8
            : 9
        )}; 
    `;
    })}

  ${({ sticky }) => (!sticky ? "border-bottom: 3px solid #000;" : "")}

  &:last-child {
    padding-right: 0;
  }

  ${({ visibleAt, theme }) =>
    visibleAt
      ? `
        display: none;
        ${theme.apply([visibleAt], (breakpoint: string) => {
          return `
              display: table-cell;        
          `;
        })}
      `
      : ""}
`;

const Td = styled.td<{
  textAlign?: string;
  verticalAlign?: string;
  visibleAt?: string;
  space?: number;
}>`
  vertical-align: ${({ verticalAlign }) => verticalAlign ?? "top"};
  padding: 0;

  border-bottom: 1px solid #000;
  text-align: ${({ textAlign }) => textAlign ?? "left"};

  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
      height: ${props.theme.spacePx(breakpoint, props.space ?? 6)};          
      padding-right: ${props.theme.spacePx(
        breakpoint,
        props.space ?? ["desktop", "screen"].includes(breakpoint)
          ? 7
          : !["base", "mobile"].includes(breakpoint)
          ? 8
          : 9
      )}; 
    `;
    })}

  &:last-child {
    padding-right: 0;
  }

  ${({ visibleAt, theme }) =>
    visibleAt
      ? `
        display: none;
        ${theme.apply([visibleAt], (breakpoint: string) => {
          return `
              display: table-cell;        
          `;
        })}
      `
      : ""}
`;

const Img = styled.img`
  position: fixed;
  top: 200px;
  left: 200px;
  pointer-events: none;
  touch-action: none;
  transform: translateY(-50%);
  display: none;
  z-index: 200;

  max-width: 300px;
  max-height: 300px;
  width: auto;
  height: auto;
  box-shadow: none;
  border-radius: 0;
  border: none;
  ${({ theme }) => theme.breakpoints.screen} {
    max-width: 400px;
    max-height: 400px;
  }

  &[src=""] {
    display: none !important;
  }
`;

export const Table = ({
  headers,
  rows,
  stickyHeader,
  useImageHover,
  heading = "h4",
  space,
}: {
  rows: TableRow[];
  headers: TableHeader[];
  stickyHeader?: boolean;
  useImageHover?: boolean;
  heading?: string;
  space?: number;
}) => {
  const config = useConfigContext();

  const imageRef = useRef() as React.MutableRefObject<HTMLImageElement>;
  const isTouchRef = useRef(false);
  const clearTouchTimoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const router = useRouter();

  const id = createUnsafeUUID();
  const onClick = (url: string, targetBlank: boolean) => {
    if (!targetBlank) {
      router.push(url);
    } else {
      window.open(url, "_blank");

      const regex = /(\.(pdf|xlsx?|docx?|txt|rtf|csv|exe|key|pp(s|t|tx)|7z|pkg|rar|gz|zip|avi|mov|mp4|mpe?g|wmv|midi?|mp3|wav|wma]))/gmi;

      if (regex.test(url)) {
        if ((window as any)?.gtag) {
          const fileExt = url.split('.').pop();
          const parsedUrl = new URL(url);
          
          (window as any)?.gtag("event", "file_download", {
            file_name: parsedUrl.pathname,
            file_extension: fileExt,
            link_url: url,
            link_text: "",
            link_id: "",
            send_to: config.ga4TagProperty,
          });
        }
      }
    }
  };

  return (
    <StyledTableContainer>
      <StyledTable>
        {headers?.length && (
          <thead>
            <Tr className={stickyHeader ? "sticky" : undefined}>
              {headers?.map((th: TableHeader, index: number) => (
                <Th
                  key={`${id}-${index}`}
                  breakpointWidth={th.breakpointWidth}
                  textAlign={th.textAlign}
                  visibleAt={th.visibleAt}
                  space={space}
                  sticky={stickyHeader}
                >
                  <Heading space={9} heading={heading ?? "h4"} inline>
                    {th.label}
                  </Heading>
                </Th>
              ))}
            </Tr>
          </thead>
        )}
        {rows?.length && (
          <tbody>
            {rows?.map((tr: any, trIndex: number) => {
              return (
                <Tr
                  key={`${id}-row-${trIndex}`}
                  isClickable={tr?.url}
                  onClick={
                    tr?.url
                      ? (event: any) => {
                          if (clearTouchTimoutRef.current) {
                            clearTimeout(clearTouchTimoutRef.current);
                          }
                          if (event?.target?.parentNode?.nodeName !== "A")
                            onClick(tr?.url, !!tr?.targetBlank);
                        }
                      : undefined
                  }
                  useImageHover={useImageHover}
                  onTouchStart={() => {
                    isTouchRef.current = true;
                    if (imageRef.current) imageRef.current.src = "";

                    if (clearTouchTimoutRef.current) {
                      clearTimeout(clearTouchTimoutRef.current);
                    }

                    clearTouchTimoutRef.current = setTimeout(() => {
                      if (clearTouchTimoutRef.current) {
                        clearTimeout(clearTouchTimoutRef.current);
                        clearTouchTimoutRef.current = null;
                        isTouchRef.current = false;
                      }
                    }, 100);
                  }}
                  onMouseEnter={
                    useImageHover && tr?.imageUrl
                      ? () => {
                          if (isTouchRef.current) return;
                          if (imageRef.current)
                            imageRef.current.src = tr?.imageUrl;
                        }
                      : undefined
                  }
                  onMouseLeave={
                    useImageHover && tr?.imageUrl
                      ? () => {
                          if (imageRef.current) imageRef.current.src = "";
                        }
                      : undefined
                  }
                  onMouseMove={
                    useImageHover && tr?.imageUrl
                      ? (event) => {
                          if (isTouchRef.current) return;
                          if (imageRef.current) {
                            imageRef.current.style.top = `${event.clientY}px`;
                            imageRef.current.style.left = `${
                              event.clientX + 50
                            }px`;

                            if (event.clientX > window.innerWidth * 0.5) {
                              imageRef.current.style.left = "auto";
                              imageRef.current.style.right = `${
                                window.innerWidth - event.clientX + 50
                              }px`;
                            } else {
                              imageRef.current.style.left = `${
                                event.clientX + 50
                              }px`;
                              imageRef.current.style.right = "auto";
                            }
                          }
                        }
                      : undefined
                  }
                >
                  {tr?.cells?.length &&
                    tr.cells.map((td: any, tdIndex: number) => (
                      <Td
                        key={`${id}-td-${trIndex}-${tdIndex}`}
                        textAlign={td.textAlign}
                        verticalAlign={td.verticalAlign}
                        visibleAt={td.visibleAt}
                        space={space}
                      >
                        {td.content}
                      </Td>
                    ))}
                </Tr>
              );
            })}
          </tbody>
        )}
      </StyledTable>
      {useImageHover && <Img src="" ref={imageRef} alt="" />}
    </StyledTableContainer>
  );
};
